// ============================================
// PANELCOTIZARRAPIDO.JS — cotizar sin salir del expediente
// ============================================
// POR QUE (medido): de 7 personas solo DOS han creado una cotizacion en la historia del
// sistema, y hay 135 expedientes abiertos sin ninguna. Contado en la pantalla real,
// cotizar desde un expediente costaba unos 10 clics y 5 pantallas, y al final habia que
// acordarse de ligar el expediente en un campo que el editor llama "(opcional)".
//
// AQUI SON TRES TOQUES: abrir · marcar · guardar.
//
// La cotizacion nace YA LIGADA. Por este camino el expediente no es opcional: viene
// puesto y no se puede vaciar. Y lo que se crea es una cotizacion NORMAL del sistema —
// al terminar se ofrece abrirla en el editor grande para afinar margenes, añadir
// productos de La Tienda o cambiar condiciones.
//
// DISEÑADO PARA 360 px ANTES QUE PARA EL ESCRITORIO, porque la idea es cotizar con el
// cliente delante, de pie, con el telefono: una columna, botones de 44 px, el total
// siempre visible pegado abajo y nada que dependa de pasar el raton por encima.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE } from '../../config/api';
import { authHeaders } from '../../utils/storage';
import useResponsive from '../../hooks/useResponsive';
import { fmtDinero } from '../../utils/dinero';
import { useBuscadorInteligente } from '../BuscadorInteligente';
import FiltroRubro, { useFiltroRubro, AvisoVacioRubro } from '../FiltroRubro';

const PP = "'Poppins', sans-serif";
const AZUL = '#265F97';
const VERDE = '#1b9e6d';
const GRIS = '#5e666e';
const GRIS_CLARO = '#9aa3ab';
const BORDE = '#e6e8ea';

const pesos = (n) => fmtDinero(n);

// Lo que el buscador inteligente mira de cada servicio (mismo motor que La Tienda, tolerante a erratas).
const candidatoDeServicio = (s) => ({ id: s.id, titulo: [s.nombre, s.categoria, s.descripcion].filter(Boolean).join(' '), marca: '', modelo: s.sku || '' });

export default function PanelCotizarRapido({ expedienteId, onCerrar, onCreada }) {
  const [datos, setDatos] = useState(null);
  const [cantidades, setCantidades] = useState({});   // { [idServicio]: cantidad }
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [hecho, setHecho] = useState(null);
  const { isMobile } = useResponsive();
  const cajaRef = useRef(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/cotizaciones/rapida/${expedienteId}/opciones`, { headers: authHeaders() })
          .then(x => x.json());
        if (!vivo) return;
        if (!r.ok) { setError(r.error || 'No se pudieron cargar los servicios.'); return; }
        setDatos(r);
      } catch { if (vivo) setError('No se pudo conectar.'); }
    })();
    return () => { vivo = false; };
  }, [expedienteId]);

  // Escape cierra. En el celular es el gesto que la gente espera del teclado fisico y en
  // escritorio evita tener que buscar la X.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !guardando) onCerrar && onCerrar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCerrar, guardando]);

  const servicios = useMemo(() => (datos ? datos.servicios : []), [datos]);
  // RUBROS (2026-10-16): el MISMO buscador inteligente de las otras pantallas (antes era un "contiene"
  // a mano) y, encima, el filtro por rubro con conteo. Sin buscar ni filtrar, el orden sigue siendo
  // el del servidor: primero lo afin al expediente, luego el resto.
  const busq = useBuscadorInteligente(servicios, candidatoDeServicio);
  const rubro = useFiltroRubro(busq.visibles);
  const busqueda = busq.q;
  const setBusqueda = busq.setQ;
  const visibles = rubro.visibles;

  const elegidos = useMemo(
    () => servicios.filter(s => (cantidades[s.id] || 0) > 0),
    [servicios, cantidades]);

  // Total EN VIVO mientras se marca. Es indicativo: el precio de verdad lo resuelve el
  // servidor con el resolutor unico al guardar. Se dice en pantalla, no se esconde.
  const totalVivo = elegidos.reduce((a, s) => a + s.precio_indicativo * (cantidades[s.id] || 0), 0);

  const poner = (id, n) => setCantidades(c => {
    const v = Math.max(0, Math.min(999, n));
    const nuevo = { ...c };
    if (v === 0) delete nuevo[id]; else nuevo[id] = v;
    return nuevo;
  });

  const guardar = useCallback(async () => {
    if (!elegidos.length || guardando) return;
    setGuardando(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/cotizaciones/rapida`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          oportunidad_id: expedienteId,
          items: elegidos.map(s => ({ tipo: 'beep_producto', referencia_id: String(s.id), cantidad: cantidades[s.id] })),
        }),
      }).then(x => x.json());
      if (!r.ok) { setError(r.error || 'No se pudo crear.'); return; }
      setHecho(r);
      onCreada && onCreada(r);
    } catch { setError('No se pudo conectar.'); }
    finally { setGuardando(false); }
  }, [elegidos, cantidades, expedienteId, guardando, onCreada]);

  const abrirEditor = (id) => {
    window.dispatchEvent(new CustomEvent('beep-editar-cotizacion', { detail: { cotizacionId: id } }));
    onCerrar && onCerrar();
  };

  // ---- estilos base ----
  const boton = {
    padding: '10px 14px', minHeight: 44, borderRadius: 10, cursor: 'pointer', fontFamily: PP,
    fontSize: 14, fontWeight: 600, border: `1px solid ${BORDE}`, background: '#fff', color: GRIS,
  };
  const botonPrincipal = { ...boton, background: AZUL, color: '#fff', borderColor: AZUL };

  return (
    <div
      onClick={(e) => { if (e.target === cajaRef.current && !guardando) onCerrar && onCerrar(); }}
      ref={cajaRef}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(20,25,30,0.45)', zIndex: 4000,
        display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
        padding: isMobile ? 0 : 20, fontFamily: PP,
      }}
    >
      <div style={{
        background: '#fff', width: '100%', maxWidth: 560,
        borderRadius: isMobile ? '16px 16px 0 0' : 16,
        // El alto se acota con la variable global del proyecto, NO con `vh` pelado: en
        // Android `vh` mide el viewport grande y con el teclado abierto el pie se sale de
        // la pantalla (es el bug de PERDIDA DE DATOS del 2026-09-12).
        maxHeight: 'calc(var(--beep-vh) * 92)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>

        {/* ---- cabecera ---- */}
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${BORDE}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#2e3338' }}>Cotizar</div>
              {datos && (
                <div style={{ fontSize: 12.5, color: GRIS_CLARO, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {datos.expediente.folio} · {datos.expediente.asunto}
                </div>
              )}
            </div>
            <button onClick={() => !guardando && onCerrar && onCerrar()}
              style={{ ...boton, minWidth: 44, padding: '10px 12px' }} aria-label="Cerrar">✕</button>
          </div>
          {/* El expediente NO es opcional por este camino: se enseña fijo, sin forma de vaciarlo. */}
          {datos && (
            <div style={{
              marginTop: 10, background: '#eef4fa', border: '1px solid #d6e4f2', borderRadius: 8,
              padding: '7px 10px', fontSize: 12.5, color: AZUL, display: 'flex', gap: 6, flexWrap: 'wrap',
            }}>
              <strong>Se guardará en este expediente</strong>
              <span style={{ color: GRIS }}>{datos.expediente.cliente || 'sin cliente'}</span>
            </div>
          )}
        </div>

        {/* ---- ya se creó ---- */}
        {hecho ? (
          <div style={{ padding: 20, textAlign: 'center', overflowY: 'auto' }}>
            <div style={{ fontSize: 40 }}>✅</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#2e3338', marginTop: 6 }}>
              {hecho.folio} creada
            </div>
            <div style={{ fontSize: 13.5, color: GRIS, marginTop: 4 }}>
              {hecho.renglones} {hecho.renglones === 1 ? 'renglón' : 'renglones'} · {pesos(hecho.total)} con IVA,
              ya ligada a este expediente.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button style={botonPrincipal} onClick={() => abrirEditor(hecho.cotizacion_id)}>
                Abrir para afinar
              </button>
              <button style={boton} onClick={() => onCerrar && onCerrar()}>Listo</button>
            </div>
          </div>
        ) : (
          <>
            {/* ---- buscador ---- */}
            <div style={{ padding: '10px 16px', flexShrink: 0 }}>
              <input
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                placeholder="Buscar un servicio…"
                inputMode="search" enterKeyHint="search" autoComplete="off"
                style={{
                  width: '100%', boxSizing: 'border-box', minHeight: 44, padding: '10px 12px',
                  borderRadius: 10, border: `1px solid ${BORDE}`, fontFamily: PP, fontSize: 14,
                }}
              />
              {busq.activo && (
                <div style={{ fontSize: 12, color: GRIS_CLARO, marginTop: 4 }} aria-live="polite">
                  {busq.buscando ? 'Buscando…' : busq.aproximado ? 'No hay nada escrito así; se muestran los parecidos.' : `${busq.visibles.length} de ${busq.total}`}
                </div>
              )}
              <FiltroRubro filtro={rubro} style={{ marginTop: 8, marginBottom: 0, paddingBottom: 2 }} />
            </div>

            {/* ---- lista ---- */}
            {/* flex:1 + minHeight:0 = la region puede ENCOGERSE dentro de la caja acotada.
                Sin esto crece con el contenido y empuja el pie fuera de la pantalla. */}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 16px 12px' }}>
              {error && !datos && <div style={{ color: '#c0392b', fontSize: 13, padding: 12 }}>{error}</div>}
              {!datos && !error && <div style={{ color: GRIS_CLARO, fontSize: 13, padding: 12 }}>Cargando…</div>}

              {datos && datos.familia_etiqueta && !busqueda && (
                <div style={{ fontSize: 12, color: GRIS_CLARO, margin: '2px 0 8px' }}>
                  Arriba, lo de <strong style={{ color: GRIS }}>{datos.familia_etiqueta}</strong>, que es de lo que va este expediente.
                </div>
              )}
              {datos && !datos.familia_etiqueta && !busqueda && (
                <div style={{ fontSize: 12, color: GRIS_CLARO, margin: '2px 0 8px' }}>
                  No pude adivinar de qué va este expediente, así que van por lo más usado.
                </div>
              )}

              {visibles.map((s, i) => {
                const n = cantidades[s.id] || 0;
                const primeroNoAfin = !busqueda && i > 0 && visibles[i - 1].afin && !s.afin;
                return (
                  <React.Fragment key={s.id}>
                    {primeroNoAfin && (
                      <div style={{ fontSize: 11.5, color: GRIS_CLARO, textTransform: 'uppercase',
                                    letterSpacing: 0.4, margin: '14px 0 6px' }}>
                        El resto del catálogo
                      </div>
                    )}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
                      borderTop: i === 0 ? 'none' : `1px solid ${BORDE}`,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: n > 0 ? 700 : 500, color: '#2e3338', wordBreak: 'break-word' }}>
                          {s.nombre}
                        </div>
                        <div style={{ fontSize: 12, color: GRIS_CLARO }}>
                          {pesos(s.precio_indicativo)}{s.unidad === 'metro' ? ' / metro' : ''}
                          {s.categoria ? ` · ${s.categoria}` : ''}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        <button onClick={() => poner(s.id, n - 1)} disabled={n === 0}
                          style={{ ...boton, minWidth: 44, padding: '8px 10px', opacity: n === 0 ? 0.35 : 1 }}
                          aria-label={`Quitar ${s.nombre}`}>−</button>
                        <div style={{ minWidth: 30, textAlign: 'center', fontSize: 15, fontWeight: 700,
                                      color: n > 0 ? AZUL : GRIS_CLARO }}>{n}</div>
                        <button onClick={() => poner(s.id, n + 1)}
                          style={{ ...boton, minWidth: 44, padding: '8px 10px',
                                   background: n > 0 ? AZUL : '#fff', color: n > 0 ? '#fff' : GRIS,
                                   borderColor: n > 0 ? AZUL : BORDE }}
                          aria-label={`Agregar ${s.nombre}`}>+</button>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}

              {datos && visibles.length === 0 && (rubro.activo
                ? <AvisoVacioRubro filtro={rubro} busqueda={busqueda} que="servicios" style={{ margin: '8px 0' }} />
                : (
                  <div style={{ color: GRIS_CLARO, fontSize: 13, padding: '16px 0', textAlign: 'center' }}>
                    {busq.sugerencia
                      ? <>Nada con «{busqueda}». ¿Quisiste decir <button type="button" onClick={() => setBusqueda(busq.sugerencia.texto)} style={{ border: 'none', background: 'none', padding: 0, color: AZUL, fontWeight: 700, cursor: 'pointer', fontFamily: PP, fontSize: 13, textDecoration: 'underline' }}>{busq.sugerencia.texto}</button>?</>
                      : <>Ningún servicio coincide con «{busqueda}».</>}
                  </div>
                ))}
            </div>

            {/* ---- pie: total en vivo + guardar. flexShrink:0 para que no lo aplaste la lista ---- */}
            <div style={{
              flexShrink: 0, borderTop: `1px solid ${BORDE}`, padding: '12px 16px',
              background: '#fafbfc', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            }}>
              <div style={{ flex: '1 1 130px', minWidth: 0 }}>
                <div style={{ fontSize: 11.5, color: GRIS_CLARO }}>
                  {elegidos.length === 0 ? 'Sin renglones'
                    : `${elegidos.length} ${elegidos.length === 1 ? 'renglón' : 'renglones'} · antes de IVA`}
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: elegidos.length ? '#2e3338' : GRIS_CLARO }}>
                  {pesos(totalVivo)}
                </div>
              </div>
              <button
                onClick={guardar}
                disabled={!elegidos.length || guardando}
                style={{
                  ...botonPrincipal, flex: '1 1 150px',
                  background: elegidos.length && !guardando ? VERDE : '#cfd6dd',
                  borderColor: elegidos.length && !guardando ? VERDE : '#cfd6dd',
                  cursor: elegidos.length && !guardando ? 'pointer' : 'default',
                }}
              >
                {guardando ? 'Creando…' : 'Crear cotización'}
              </button>
              {error && <div style={{ flexBasis: '100%', color: '#c0392b', fontSize: 12.5 }}>{error}</div>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
